import { Pencil } from 'lucide-react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ResizableTh } from '@/components/table'
import { cn } from '@/lib/utils'
import { CalendarCell, CycleCell } from './gridCells'
import {
  CYCLE_LABELS,
  CYCLE_ORDER,
  type Cycle,
  type EquipmentTypeRow,
  type ResolveEventMeta,
} from '@/types/equipment'
import {
  EQUIPMENT_SCOPE,
  ROOM_SCOPE,
  STORAGE_SCOPE,
  useEquipmentScope,
  type EquipmentCopy,
  type EquipmentScope,
} from './equipmentScope'

const HEAD_CELL = 'whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide'

// Per-user column layout. Equipment Type is locked (it identifies the row) and
// so is the Edit cell. See src/components/table/index.ts for the pattern.
// One layout per page (z8nrz7ewt3): the Cleaning page keeps 'room-types'.
const tableIdFor = (scope: EquipmentScope) => `${scope.tablePrefix}-types`

/** Column id for one service cycle, namespaced against the fixed column ids. */
function cycleColumnId(cycle: Cycle): string {
  return `cycle:${cycle}`
}

const columnsFor = (copy: EquipmentCopy): TableColumnDef[] => [
  { id: 'equipmentType', header: copy.type, locked: true, sortable: true },
  { id: 'group', header: copy.group, sortable: true },
  { id: 'units', header: copy.unitCount, sortable: true },
  ...CYCLE_ORDER.map(c => ({ id: cycleColumnId(c), header: CYCLE_LABELS[c] })),
  { id: 'calendar', header: 'Calendar' },
  { id: 'actions', header: 'Edit', locked: true },
]

// Built once per page: the column-prefs hook keys its memo on array identity.
const COLUMNS = {
  equipment: columnsFor(EQUIPMENT_SCOPE.copy),
  room: columnsFor(ROOM_SCOPE.copy),
  storage: columnsFor(STORAGE_SCOPE.copy),
} satisfies Record<EquipmentScope['variant'], TableColumnDef[]>

// Header sorting (z8nrz7e4h6), applied on top of the toolbar's sort order.
// Cycle and Calendar cells are schedule widgets, not values — unsortable.
const EQUIPMENT_TYPES_SORT: SortAccessors<EquipmentTypeRow> = {
  equipmentType: r => r.equipmentType,
  group: r => r.group,
  units: r => r.units,
}

const TYPE_HEAD_EXTRA: Record<string, string | undefined> = {
  units: 'text-center',
  calendar: 'border-l border-white/20',
  actions: 'border-l border-white/20 text-center',
  ...Object.fromEntries(CYCLE_ORDER.map(c => [cycleColumnId(c), 'border-l border-white/20'])),
}

/**
 * The FULL type list in the order this grid's headers chose (z8nrz7e4h6) — the
 * planner pages before the grid sees it, so the page sorts the whole list with
 * this and pages the result. See useEquipmentGridSort.
 */
export function useEquipmentTypeGridSort(rows: EquipmentTypeRow[]): {
  rows: EquipmentTypeRow[]
  sortKey: string
} {
  const scope = useEquipmentScope()
  const cols = useTableColumns(tableIdFor(scope), COLUMNS[scope.variant])
  const sorted = useSortedRows(rows, cols.sort, EQUIPMENT_TYPES_SORT)
  return { rows: sorted, sortKey: cols.sort ? `${cols.sort.id}:${cols.sort.dir}` : '' }
}

export function EquipmentTypeGrid({
  rows,
  resolveMeta,
  onConfigure,
  onCalendar,
  onEdit,
}: {
  rows: EquipmentTypeRow[]
  resolveMeta: ResolveEventMeta
  onConfigure: (equipmentType: string, cycle: Cycle) => void
  onCalendar: (equipmentType: string) => void
  onEdit: (row: EquipmentTypeRow) => void
}) {
  const scope = useEquipmentScope()
  const cols = useTableColumns(tableIdFor(scope), COLUMNS[scope.variant])
  const sortedRows = useSortedRows(rows, cols.sort, EQUIPMENT_TYPES_SORT)
  // The "not maintained" row replaces the whole service block with one spanning
  // cell, so the span follows what the user has left visible.
  const serviceSpan =
    CYCLE_ORDER.filter(c => cols.isVisible(cycleColumnId(c))).length +
    (cols.isVisible('calendar') ? 1 : 0)
  return (
    <>
    <div className='overflow-x-persistent rounded-lg border border-gray-200'>
      <table className='w-full min-w-[1300px] border-collapse text-sm'>
        <thead className='sticky top-0 z-10 bg-primary text-primary-foreground'>
          <tr>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={cn(HEAD_CELL, TYPE_HEAD_EXTRA[def.id])}
                resizable={def.id !== 'actions'}
              >
                {def.header}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr
              key={row.equipmentType}
              className={cn(
                'border-b border-gray-100 transition-colors hover:bg-blue-50/60',
                i % 2 === 1 && 'bg-gray-50/60',
              )}
            >
              {/* Guarded cell by cell rather than through ColumnCells: the
                  "not maintained" branch below collapses the cycle block into
                  ONE spanning cell, which a one-id-per-cell map cannot express. */}
              {cols.isVisible('equipmentType') && (
                <td className='px-3 py-2.5 align-top text-xs font-semibold text-gray-800'>
                  {row.equipmentType}
                </td>
              )}
              {cols.isVisible('group') && (
                <td className='px-3 py-2.5 align-top text-xs text-gray-500'>{row.group}</td>
              )}
              {cols.isVisible('units') && (
                <td className='px-3 py-2.5 text-center align-top'>
                  <span className='inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-semibold text-gray-600'>
                    {row.units}
                  </span>
                </td>
              )}
              {/* A type nobody services gets one spanning cell saying so,
                  rather than seven empty ones inviting a click that the API
                  would refuse. The row stays in the table — it is ordinary
                  equipment, it just has no service cycle. */}
              {row.maintained ? (
                <>
                  {CYCLE_ORDER.filter((cycle) => cols.isVisible(cycleColumnId(cycle))).map((cycle) => (
                    <td key={cycle} className='border-l border-gray-100 px-3 py-2.5 align-top'>
                      <CycleCell
                        entry={row.schedule[cycle]}
                        resolveMeta={resolveMeta}
                        onClick={() => onConfigure(row.equipmentType, cycle)}
                      />
                    </td>
                  ))}
                  {cols.isVisible('calendar') && (
                    <td className='border-l border-gray-100 px-3 py-2.5 align-top'>
                      <CalendarCell
                        info={row.calendar}
                        onClick={() => onCalendar(row.equipmentType)}
                      />
                    </td>
                  )}
                </>
              ) : serviceSpan > 0 ? (
                <td
                  colSpan={serviceSpan}
                  className='border-l border-gray-100 px-3 py-2.5 align-middle'
                >
                  <span className='inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500'>
                    {scope.copy.notOnCycle}
                  </span>
                </td>
              ) : null}
              {cols.isVisible('actions') && (
                <td className='border-l border-gray-100 px-3 py-2.5 text-center align-top'>
                  <button
                    type='button'
                    onClick={() => onEdit(row)}
                    title={`Edit ${scope.copy.unitLower} type`}
                    className='inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500'
                  >
                    <Pencil className='h-4 w-4' />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.visible.length} className='px-3 py-10 text-center text-sm text-gray-400'>
                {scope.copy.noTypeMatch}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </>
  )
}
